// Web traffic year-over-year comparison.
//
// Traffic is a demand proxy, and demand is seasonal: a retailer's December
// always beats its November, and a tax-software site's April always beats
// everything. Month-over-month therefore reads seasonality as trend. The only
// honest comparison is the SAME calendar month one year apart, which is what
// this module is built around — and what the `trafficChange` column has always
// claimed to show.
//
// It never did. `screen_query` fetched a 12-month window while the old
// implementation required a comparison point at least 10 months older than the
// newest one; with real coverage the widest available span was 9.1 months, so
// the threshold could not be met and every stock got null (issue #122). Worse,
// "closest to 12 months back, at least 10 months back" is a fuzzy match — when
// it did fire it was free to compare December against February and call the
// seasonal swing a trend.
//
// Coverage is also fragmented (issues #118/#119): 2025-03 → 2025-09 was never
// collected and cannot be recovered, since the provider retains only three
// complete months. So a stock's newest month usually has NO prior-year
// counterpart. Everything here therefore reports which months it actually
// compared instead of assuming the newest month is comparable.

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// trafficDate arrives as 'YYYY-MM-DD'. Reading the month via `new Date(...)`
// would parse it as UTC midnight and then report the LOCAL month, which is the
// PREVIOUS month for every user west of Greenwich — 2025-12-01 becomes November
// in California. Split the string instead; a month label has no timezone.
const parseMonthKey = (trafficDate) => {
  const match = /^(\d{4})-(\d{2})/.exec(String(trafficDate ?? ''));
  if (!match) return null;
  const monthIndex = Number(match[2]) - 1;
  if (monthIndex < 0 || monthIndex > 11) return null;
  return { year: Number(match[1]), monthIndex };
};

const cellKey = (year, monthIndex) => `${year}-${monthIndex}`;

// One definition of the percentage. The table column, the panel headline and
// the chart tooltip all report a same-month year-over-year change, and each
// recomputing it by hand is how three surfaces quietly start disagreeing.
// Callers must have already dropped visits <= 0, so `previous` is never zero.
export const pctChange = (current, previous) => ((current - previous) / previous) * 100;

/**
 * Reshapes raw `site_traffic` rows into the same-month-across-years view.
 *
 * @param {Array<{trafficDate: string, visits: number}>} trafficData
 * @returns {null | {
 *   years: number[],
 *   months: Array<Object>,      // one row per calendar month, keyed by year for Recharts
 *   pairs: Array<Object>,       // every matched same-month/adjacent-year pair, oldest first
 *   latestPair: Object|null,    // the most recent matched pair — the headline comparison
 *   latestMonth: {year, monthIndex, label, visits}|null,   // newest collected month, paired or not
 *   earliestMonth: {year, monthIndex, label, visits}|null, // oldest collected month
 * }}
 */
export const buildTrafficYoY = (trafficData) => {
  if (!Array.isArray(trafficData) || trafficData.length === 0) return null;

  // 3.9% of rows carry visits = 0 — sites below the provider's measurement
  // floor, not sites that lost all their traffic. Dropping them keeps a zero
  // bar from reading as a collapse, and keeps a zero out of the denominator
  // where it would make every change infinite.
  const visitsByCell = new Map();
  const years = new Set();
  for (const row of trafficData) {
    const parsed = parseMonthKey(row?.trafficDate);
    const visits = Number(row?.visits);
    if (!parsed || !Number.isFinite(visits) || visits <= 0) continue;
    visitsByCell.set(cellKey(parsed.year, parsed.monthIndex), visits);
    years.add(parsed.year);
  }
  if (visitsByCell.size === 0) return null;

  const sortedYears = [...years].sort((a, b) => a - b);

  // One row per calendar month so the x-axis is the seasonal cycle and the same
  // month sits directly above itself across years. Months nobody collected are
  // dropped entirely rather than emitted as zero — an uncollected month and a
  // month with no visitors must never look alike.
  const months = [];
  for (let monthIndex = 0; monthIndex < 12; monthIndex += 1) {
    const row = { monthIndex, label: MONTH_LABELS[monthIndex] };
    let present = 0;
    for (const year of sortedYears) {
      const visits = visitsByCell.get(cellKey(year, monthIndex));
      if (visits != null) {
        row[String(year)] = visits;
        present += 1;
      }
    }
    if (present === 0) continue;
    months.push(row);
  }

  // A pair is a pair only when both sides are the same calendar month exactly
  // one year apart. No nearest-neighbour matching: a comparison the reader
  // can't name is a comparison they can't check.
  const pairs = [];
  for (const year of sortedYears) {
    for (let monthIndex = 0; monthIndex < 12; monthIndex += 1) {
      const current = visitsByCell.get(cellKey(year, monthIndex));
      const previous = visitsByCell.get(cellKey(year - 1, monthIndex));
      if (current == null || previous == null) continue;
      pairs.push({
        year,
        monthIndex,
        label: MONTH_LABELS[monthIndex],
        periodLabel: `${MONTH_LABELS[monthIndex]} ${year}`,
        priorPeriodLabel: `${MONTH_LABELS[monthIndex]} ${year - 1}`,
        current,
        previous,
        changePct: pctChange(current, previous),
      });
    }
  }
  pairs.sort((a, b) => a.year - b.year || a.monthIndex - b.monthIndex);

  // The newest collected month is usually NEWER than the newest comparable one
  // (the 2025-03 → 2025-09 hole). Callers surface the difference so nobody
  // reads a Dec-2025 headline as a statement about today.
  //
  // The EARLIEST month matters for the opposite case. 46% of symbols were first
  // collected in 2025-10 or later and so have no prior year at all; for those,
  // the earliest month on file is what says when a comparison first becomes
  // possible (that month, one year on).
  let latestMonth = null;
  let earliestMonth = null;
  for (const year of sortedYears) {
    for (let monthIndex = 0; monthIndex < 12; monthIndex += 1) {
      const visits = visitsByCell.get(cellKey(year, monthIndex));
      if (visits == null) continue;
      const entry = { year, monthIndex, label: `${MONTH_LABELS[monthIndex]} ${year}`, visits };
      if (!latestMonth || year > latestMonth.year || (year === latestMonth.year && monthIndex > latestMonth.monthIndex)) {
        latestMonth = entry;
      }
      if (!earliestMonth || year < earliestMonth.year || (year === earliestMonth.year && monthIndex < earliestMonth.monthIndex)) {
        earliestMonth = entry;
      }
    }
  }

  return {
    years: sortedYears,
    months,
    pairs,
    latestPair: pairs.length ? pairs[pairs.length - 1] : null,
    latestMonth,
    earliestMonth,
  };
};

// Both consumers — the `trafficChange` table column in api.js and the detail
// panel — read `latestPair` off the object above rather than going through a
// convenience wrapper. That is deliberate: the column needs the pair's visit
// count and period label as well as its percentage, and a wrapper returning
// only the percentage is how the column ended up describing a different month
// from the number printed beside it.
